import { Button, DatePicker, Form, Grid, Input, Select } from 'antd';

import {
  ApplicantProfileProfessionEditContainerApplicantUserProfessionalFieldsFragment,
  ApplicantUserPrimaryEducationalInformation,
  ApplicantUserProfessionalInput,
  Entity
} from '../../../../generated';
import { MinusCircleOutlined } from '@ant-design/icons';
import React, { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

import { Country } from '../../../../types/country';
import { Rule } from 'antd/es/form';
import { EditProfile } from './edit-profile';
import { StateSelect } from '../shared/components/state-select';
import { HealthProfession } from '../../../../types/healthProfession';
import { APOLLO_DATE_FORMAT, DATE_FORMAT } from '../../../../constants';
import { DateInformationFooter } from '../../../shared/date-information-footer';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
const EDIT_TITLE = 'Edit Professional Details';
interface ProfessionEditProps {
  professionalInformation: ApplicantProfileProfessionEditContainerApplicantUserProfessionalFieldsFragment;
  countryData: Country[];
  entities: Entity[];
  healthProfessions: HealthProfession[];
  onCancel: () => void;
  onSave: (applicant: ApplicantUserProfessionalInput) => Promise<void>;
  fetchProfessionData: () => any;
}
const formatApolloDate = (date: string) => {
  return dayjs(date).utc().format(APOLLO_DATE_FORMAT).toUpperCase();
};

export const ProfileProfessionEdit: React.FC<ProfessionEditProps> = ({
  professionalInformation,
  countryData,
  onCancel,
  onSave,
  entities,
  healthProfessions,
  fetchProfessionData
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [formError] = React.useState<string>('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [zipCodeRules, setZipCodeRules] = useState<Rule[][]>(professionalInformation?.primaryEducationalInformation?.map(() => [{ required: false, message: '' }]) ?? []);

  const [showOtherSection, setShowOtherSection] = useState<boolean[]>(
    professionalInformation.primaryEducationalInformation?.map(
      (educationDetail) => educationDetail?.issuingInstitution === null && educationDetail?.otherIssuingInstitution !== null
    ) ?? []
  );
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const initalValues = {
    healthProfession: professionalInformation?.healthProfession,
    citizenshipAtTimeEnteredMedicalInstitution: professionalInformation?.citizenshipAtTimeEnteredMedicalInstitution,
    primaryEducationalInformation: professionalInformation?.primaryEducationalInformation
  };

  const fetchProfData = async () => {
    const profData: ApplicantProfileProfessionEditContainerApplicantUserProfessionalFieldsFragment = await fetchProfessionData();
    return profData;
  };

  useEffect(() => {
    const initialCountries = form.getFieldsValue().primaryEducationalInformation?.map((educationDetail: any) => educationDetail?.otherIssuingInstitution?.country || '') || [];
    console.log('initialCountries', initialCountries);

    const initialZipCodeRules = initialCountries.map((country: string) => {
      return country === 'US' || country === 'CA' ? [{ required: true, message: 'Please enter Zip/Postal Code' }] : [{ required: false, message: '' }];
    });

    setSelectedCountries(initialCountries);
    setZipCodeRules(initialZipCodeRules);
  }, []);

  useEffect(() => {
    const professionalInformation = fetchProfData();
    professionalInformation.then((profData) => {
      profData?.primaryEducationalInformation?.forEach((educationDetail, index) => {
        if (educationDetail?.issuingInstitution?.id === undefined && educationDetail?.otherIssuingInstitution !== null) {
          form.setFieldValue(['primaryEducationalInformation', index, 'issuingInstitution', 'id'], 'Other');
        }
      });
    });
  }, []);

  const handleCountryChange = (value: string, index: number) => {
    setSelectedCountries((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    // Reset state / province when country changes
    form.setFieldsValue({
      primaryEducationalInformation: form.getFieldsValue().primaryEducationalInformation.map((educationDetail: any, i: number) => {
        if (i === index) {
          return {
            ...educationDetail,
            otherIssuingInstitution: {
              ...educationDetail.otherIssuingInstitution,
              stateOrProvince: ''
            }
          };
        }
        return educationDetail;
      })
    });

    setZipCodeRules((prev) => {
      const updatedRules = [...prev];
      if (value === 'US' || value === 'CA') {
        updatedRules[index] = [{ required: true, message: 'Please enter Zip/Postal Code' }];
      } else {
        updatedRules[index] = [{ required: false, message: '' }];
      }
      return updatedRules;
    });
  };

  const [educationDetails, setEducationDetails] = React.useState<ApplicantUserPrimaryEducationalInformation[]>(
    professionalInformation?.primaryEducationalInformation as ApplicantUserPrimaryEducationalInformation[]
  );

  const normalizeGraduationYear = (value: number | Dayjs) => {
    if (typeof value === 'number') {
      return { value: dayjs().year(value).utc() };
    }
    if (typeof value !== 'string') { // dayjs type
      return {value};
    }
    return { value: null };
  }

  const onEducationDetailsFieldChange = (changedValues: unknown, key: string, index: number) => {
    const updatedEducationDetails = educationDetails.map((educationDetail, i) => {
      if (i === index) {
        if (key === 'issuingInstitution') {
          setShowOtherSection((prev) => {
            const updated = [...prev];
            updated[index] = changedValues === 'Other';
            return updated;
          });
        }
        return {
          ...educationDetail,
          [key]: changedValues
        };
      }
      return educationDetail;
    });
    setEducationDetails(updatedEducationDetails);
  };

  const ProfessionalDetailsForm = (
    <Form
      form={form}
      style={{ width: '100%' }}
      initialValues={initalValues}
      layout="vertical"
      onFinish={async (values) => {
        const professionalInfo: ApplicantUserProfessionalInput = {
          citizenshipAtTimeEnteredMedicalInstitution: values.citizenshipAtTimeEnteredMedicalInstitution,
          healthProfession: values.healthProfession
        };
        professionalInfo.primaryEducationalInformation = values.primaryEducationalInformation.map((eduInfo: ApplicantUserPrimaryEducationalInformation) => {
          return {
            attendanceEndedAt: formatApolloDate(eduInfo.attendanceEndedAt),
            attendanceStartedAt: formatApolloDate(eduInfo.attendanceStartedAt),
            degreeIssuedAt: formatApolloDate(eduInfo.degreeIssuedAt),
            degreeTitle: eduInfo.degreeTitle,
            graduationYear: typeof eduInfo.graduationYear === 'number' ? eduInfo.graduationYear : dayjs(eduInfo.graduationYear).year(),
            issuingInstitutionId: eduInfo.issuingInstitution?.id !== 'Other' ? eduInfo.issuingInstitution?.id : null,
            otherIssuingInstitution:
              eduInfo.issuingInstitution?.id === 'Other'
                ? {
                    addressLine1: eduInfo.otherIssuingInstitution?.addressLine1,
                    city: eduInfo.otherIssuingInstitution?.city,
                    country: eduInfo.otherIssuingInstitution?.country,
                    name: eduInfo.otherIssuingInstitution?.name,
                    stateOrProvince: eduInfo.otherIssuingInstitution?.stateOrProvince,
                    zipOrPostalCode: eduInfo.otherIssuingInstitution?.zipOrPostalCode
                  }
                : null,
            id: eduInfo.id
          };
        });
        onSave(professionalInfo);
        setIsSubmitting(true);
      }}
    >
      <Form.Item
        name={['healthProfession']}
        validateTrigger={['onChange', 'onBlur']}
        rules={[
          {
            required: true,
            message: 'Please select a health profession'
          }
        ]}
        label="Please select your profession of practice."
      >
        <Select placeholder="Select Health Profession" style={{ width: '70%' }}>
          {healthProfessions.map((healthProf) => (
            <Select.Option key={healthProf.professionCode} value={healthProf.professionCode}>
              {healthProf.professionName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name={['citizenshipAtTimeEnteredMedicalInstitution']}
        validateTrigger={['onChange', 'onBlur']}
        rules={[
          {
            required: true,
            message: 'Please select Citizenship Upon Entering Educational Institution'
          }
        ]}
        label="Citizenship Upon Entering Educational Institution"
      >
        <Select style={{ width: '70%' }} placeholder="Select Country">
          {countryData.map((item: Country) => (
            <Select.Option key={item.countryCode} value={item.countryCode}>
              {item.countryName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <p style={{ width: '90%' }} className="ant-form-item-label">
        Primary Educational Institution Information (Minimum 1 - Max 5)
      </p>
      <Form.List
        name={['primaryEducationalInformation']}
        rules={[
          {
            validator: async (_, educationDetails) => {
              console.log('primary educationDetails', educationDetails);
              if (!educationDetails || educationDetails.length < 1) {
                return Promise.reject(new Error('Please add at least one primary educational information'));
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <div
                    key={field.key}
                    style={{
                      marginBottom: '15px',
                      display: 'flex',
                      width: '100%'
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        paddingRight: '10px'
                      }}
                    >
                      {educationDetails.length > 1 ? (
                        <MinusCircleOutlined
                          className="dynamic-delete-button"
                          onClick={() => {
                            setEducationDetails((prev) => prev.filter((_, i) => i !== index));
                            setShowOtherSection((prev) => prev.filter((_, i) => i !== index));
                            remove(field.name);
                          }}
                        />
                      ) : null}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        paddingTop: '3px',
                        paddingLeft: '10px',
                        borderLeft: '1px solid darkgrey'
                      }}
                    >
                      <Form.Item name={[index, 'id']} hidden={true}>
                        <Input hidden={true} />
                      </Form.Item>
                      <Form.Item
                        name={[index, 'degreeTitle']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter Degree Title'
                          }
                        ]}
                        label="Degree Title"
                      >
                        <Input style={{ width: 200 }} onChange={(e) => onEducationDetailsFieldChange(e.target.value, 'degreeTitle', index)} placeholder="Degree Title" />
                      </Form.Item>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Form.Item
                          name={[index, 'attendanceStartedAt']}
                          valuePropName="value"
                          rules={[
                            {
                              required: true,
                              message: 'Please select Attendance Start Date'
                            }
                          ]}
                          label="Attendance Start Date"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value).utc() : ''
                          })}
                        >
                          <DatePicker
                            style={{ width: '90%' }}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.attendanceStartedAt
                                ? dayjs(professionalInformation?.primaryEducationalInformation?.[index]?.attendanceStartedAt).format(DATE_FORMAT)
                                : ''
                            }
                            maxDate={dayjs(educationDetails[index].attendanceEndedAt || dayjs().utc(), DATE_FORMAT)}
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            onChange={(date) => onEducationDetailsFieldChange(dayjs(date).utc().format(DATE_FORMAT), 'attendanceStartedAt', index)}
                          />
                        </Form.Item>
                        <Form.Item
                          name={[index, 'attendanceEndedAt']}
                          valuePropName="value"
                          rules={[
                            {
                              required: true,
                              message: 'Please select Attendance End Date'
                            }
                          ]}
                          label="Attendance End Date"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value).utc() : ''
                          })}
                        >
                          <DatePicker
                            style={{ width: '90%' }}
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            minDate={dayjs(educationDetails[index].attendanceStartedAt).utc()}
                            maxDate={dayjs(dayjs().utc().endOf('day'), DATE_FORMAT)}
                            onChange={(date) => onEducationDetailsFieldChange(dayjs(date).utc().format(DATE_FORMAT), 'attendanceEndedAt', index)}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.attendanceEndedAt
                                ? dayjs(professionalInformation?.primaryEducationalInformation?.[index]?.attendanceEndedAt).format(DATE_FORMAT)
                                : ''
                            }
                          />
                        </Form.Item>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Form.Item
                          name={[index, 'degreeIssuedAt']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select Date Degree Issued'
                            }
                          ]}
                          label="Date Degree Issued"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value).utc() : ''
                          })}
                        >
                          <DatePicker
                            style={{ width: '90%' }}
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            minDate={dayjs(educationDetails[index].attendanceEndedAt).utc()}
                            maxDate={dayjs(dayjs().utc(), DATE_FORMAT)}
                            onChange={(date) => onEducationDetailsFieldChange(dayjs(date).utc().format(DATE_FORMAT), 'degreeIssuedAt', index)}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.degreeIssuedAt
                                ? dayjs(professionalInformation?.primaryEducationalInformation?.[index]?.degreeIssuedAt).format(DATE_FORMAT)
                                : ''
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name={[index, 'graduationYear']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select Graduation Year'
                            }
                          ]}
                          label="Graduation Year"
                          getValueProps={(value) => {
                            return  normalizeGraduationYear (value)
                          }}
                        >
                          <DatePicker
                            style={{ width: '90%' }}
                            picker="year"
                            format={'YYYY'}
                            maxDate={dayjs(dayjs().utc(), 'YYYY')}
                            onChange={(date) => onEducationDetailsFieldChange(dayjs(date).utc().format('YYYY'), 'graduationYear', index)}
                            // value={
                            //   professionalInformation?.primaryEducationalInformation?.[index]?.graduationYear
                            //     ? dayjs(professionalInformation?.primaryEducationalInformation?.[index]?.graduationYear).format('YYYY')
                            //     : ''
                            // }
                          />
                        </Form.Item>
                      </div>
                      <Form.Item
                        name={[index, 'issuingInstitution', 'id']}
                        rules={[
                          {
                            required: true,
                            message: 'Please select Issuing Institution'
                          },
                          {
                            validator: async (_, value: string) => {
                              const selectedValues = form.getFieldValue('issuingInstitution');
                              if (selectedValues && selectedValues.filter((val: string) => val === value).length > 1) {
                                return Promise.reject(new Error('Duplicate selection is not allowed'));
                              }
                            }
                          }
                        ]}
                        label="Issuing Institution"
                      >
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Issuing Institution"
                          onSelect={(issuingInstitution) => {
                            console.log('issuingInstitution', issuingInstitution);
                            onEducationDetailsFieldChange(issuingInstitution, 'issuingInstitution', index);
                          }}
                          defaultValue={showOtherSection[index] ? 'Other' : educationDetails[index]?.issuingInstitution?.entityName}
                        >
                          {entities.map((entity) => (
                            <Select.Option key={entity.id} value={entity.id}>
                              {entity.entityName}
                            </Select.Option>
                          ))}
                          <Select.Option key="Other" value="Other">
                            Other
                          </Select.Option>
                        </Select>
                      </Form.Item>
                      {showOtherSection[index] && (
                        <>
                          <Form.Item
                            style={{ width: '100%' }}
                            name={[index, 'otherIssuingInstitution', 'name']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter Institution Name'
                              }
                            ]}
                            label="Institution Name"
                          >
                            <Input
                              style={{ width: '90%' }}
                              onChange={(otherIssuingInstitutionName) => onEducationDetailsFieldChange(otherIssuingInstitutionName, 'name', index)}
                              placeholder="Institution Name"
                              maxLength={64}
                            />
                          </Form.Item>
                          <Form.Item
                            style={{ width: '100%' }}
                            name={[index, 'otherIssuingInstitution', 'addressLine1']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter Address'
                              }
                            ]}
                            label="Address"
                          >
                            <Input
                              style={{ width: '90%' }}
                              onChange={(otherIssuingInstitutionAddress) => onEducationDetailsFieldChange(otherIssuingInstitutionAddress, 'addressLine1', index)}
                              placeholder="Address"
                              maxLength={64}
                            />
                          </Form.Item>
                          <Form.Item
                            name={[index, 'otherIssuingInstitution', 'country']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Please select Country'
                              }
                            ]}
                            label="Country"
                            style={{ width: '100%' }}
                            initialValue={professionalInformation?.primaryEducationalInformation?.[index]?.otherIssuingInstitution?.country}
                          >
                            <Select
                              showSearch
                              optionFilterProp="children"
                              style={{ width: '90%' }}
                              placeholder="Country"
                              onChange={(otherIssuingInstitutionCountry) => {
                                onEducationDetailsFieldChange(otherIssuingInstitutionCountry, 'country', index);
                                handleCountryChange(otherIssuingInstitutionCountry, index);
                              }}
                            >
                              {countryData.map((item: Country) => (
                                <Select.Option key={item.countryCode} value={item.countryCode}>
                                  {item.countryName}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <div
                            style={{
                              display: 'flex',
                              gap: '10px',
                              width: '100%',
                              flexDirection: isMobile ? 'column' : 'row'
                            }}
                          >
                            <Form.Item
                              label="City"
                              name={[index, 'otherIssuingInstitution', 'city']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter City'
                                }
                              ]}
                              validateTrigger={['onChange', 'onBlur']}
                            >
                              <Input
                                style={{ width: '90%' }}
                                onChange={(otherIssuingInstitutionCity) => onEducationDetailsFieldChange(otherIssuingInstitutionCity, 'city', index)}
                                placeholder="City"
                                maxLength={50}
                              />
                            </Form.Item>
                            <StateSelect
                              style={{ width: '90%' }}
                              name={[index, 'otherIssuingInstitution', 'stateOrProvince']}
                              selectedCountry={selectedCountries[index]} // Pass the corresponding country
                              countryData={countryData}
                            />
                            <Form.Item
                              label="Zip/Postal Code"
                              name={[index, 'otherIssuingInstitution', 'zipOrPostalCode']}
                              rules={zipCodeRules[index]}
                              validateTrigger={['onChange', 'onBlur']}
                            >
                              <Input
                                style={{ width: '90%' }}
                                maxLength={10}
                                placeholder="Zip/Postal Code"
                                onChange={(otherIssuingInstitutionZip) => onEducationDetailsFieldChange(otherIssuingInstitutionZip, 'zipOrPostalCode', index)}
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {fields.length < 5 ? (
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      setEducationDetails([
                        ...educationDetails,
                        {
                          id: '',
                          degreeTitle: '',
                          attendanceStartedAt: '',
                          attendanceEndedAt: '',
                          degreeIssuedAt: '',
                          issuingInstitution: { id: '', entityName: '' },
                          otherIssuingInstitution: {
                            name: '',
                            addressLine1: '',
                            city: '',
                            stateOrProvince: '',
                            zipOrPostalCode: '',
                            country: ''
                          }
                        }
                      ]);
                      setShowOtherSection((prev) => [...prev, false]);
                      add();
                    }}
                    block
                    style={{ width: '90%', paddingRight: '10px' }}
                  >
                    Other Educational Institution
                  </Button>
                </Form.Item>
              ) : (
                <></>
              )}
            </div>
          );
        }}
      </Form.List>
      {formError && educationDetails.length < 1 && <p style={{ color: '#ff4d4f' }}>{formError}</p>}
      <div className="flex items-center justify-end gap-4 w-full" {...(isMobile && { style: { marginBottom: '25px' } })}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button htmlType="submit" type="primary" value={'save'} loading={isSubmitting}>
          Save
        </Button>
      </div>
      <DateInformationFooter
        customStyle={{
          right: 0,
          width: '100%'
        }}
      />
    </Form>
  );

  return (
    <div style={{ maxHeight: isMobile ? '75vh' : '80vh', overflowY: 'auto' }}>
      <EditProfile title={EDIT_TITLE} form={ProfessionalDetailsForm} />
    </div>
  );
};
